import { createMultipleFilter, createSingleFilter } from "app/utils/filters";

import {
  IS_ONE_OF_OPERATOR,
  NEGATION_TO_POSITIVE_MAP,
} from "./notificationOperatorNegation";

import type { CriteriaFilterValue } from "@stacklet/ui";
import type { FilterElementInput } from "__generated__/AppQuery.graphql";

/**
 * Creates a FilterElementInput for a single filter value.
 *
 * "is one of" is expanded to OR'd equals filters so the structure is
 * unambiguous on round-trip read-back (OR vs a plain single with a
 * comma-separated value). "is not one of" recurses into "is one of"
 * and wraps the result with NOT → NOT(OR(equals, equals, ...)).
 * Other negating operators are wrapped as `NOT(positive single)`.
 *
 * Operator normalization (hyphen → space) is performed here so callers
 * can pass operators in either form ("is-one-of" or "is one of").
 */
function buildFilterElement(
  name: string,
  value: string | undefined,
  operator: string | undefined,
): FilterElementInput | undefined {
  if (!value) return undefined;

  // Normalize to space-separated form — the form layer stores operators as hyphenated
  // (e.g. "is-one-of") but the API and all map lookups use space-separated form.
  const normalizedOperator = operator?.trim().toLowerCase().replace(/-/g, " ");

  // "is one of" → OR(equals, ...) always, even for a single value, so the structure
  // is unambiguous on round-trip read-back (OR vs a plain single(equals)).
  if (normalizedOperator === IS_ONE_OF_OPERATOR) {
    const values = value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    if (values.length === 0) return undefined;
    const operands = values
      .map((v) => createSingleFilter(name, v, "equals"))
      .filter((f): f is FilterElementInput => f !== undefined);
    if (operands.length === 0) return undefined;
    return { multiple: { operator: "OR", operands } };
  }

  if (normalizedOperator) {
    const positiveOp = NEGATION_TO_POSITIVE_MAP[normalizedOperator];
    if (positiveOp !== undefined) {
      // Recursively build the positive form, then wrap with NOT.
      // For "is not one of": positiveOp is "is one of", which recurses to produce
      // OR(equals, ...), giving the final NOT(OR(equals, ...)) structure.
      const inner = buildFilterElement(name, value, positiveOp);
      if (!inner) return undefined;
      return { multiple: { operator: "NOT", operands: [inner] } };
    }
  }

  return createSingleFilter(name, value, normalizedOperator);
}

export function isTagCriteria(criteria: string): boolean {
  return criteria === "account-tag" || criteria === "resource-tag";
}

/**
 * Converts an array of CriteriaFilterValues to a FilterElementInput.
 * Each value produces one filter element; all are combined with AND.
 */
export function criteriaFilterValuesToFilterElement(
  criteriaFilterValues: CriteriaFilterValue[],
): FilterElementInput | null {
  if (criteriaFilterValues.length === 0) {
    return null;
  }

  const filterElements: FilterElementInput[] = [];

  for (const cv of criteriaFilterValues) {
    const valueStr = (cv.value ?? [])
      .flatMap((v) => (v?.id ? [v.id] : []))
      .join(",");

    let element: FilterElementInput | undefined;

    if (isTagCriteria(cv.criteria)) {
      // Tag only: { name: "resource-tag" | "account-tag", value: tagKey }
      // Tag + value: { name: "resource-tag:tagKey" | "account-tag:tagKey", operator, value }
      element =
        valueStr === ""
          ? createSingleFilter(cv.criteria, cv.name)
          : buildFilterElement(
              `${cv.criteria}:${cv.name}`,
              valueStr,
              cv.operator ?? undefined,
            );
    } else {
      element = buildFilterElement(
        cv.criteria,
        valueStr || undefined,
        cv.operator ?? undefined,
      );
    }

    if (element) {
      filterElements.push(element);
    }
  }

  if (filterElements.length === 0) {
    return null;
  }

  if (filterElements.length === 1) {
    return filterElements[0];
  }

  // Combine all criteria+operator groups with AND
  return createMultipleFilter(filterElements, "AND") ?? null;
}
